// The content-editor panel ("On the website") as DATA.
//
// A transaction with a content profile (`transaction.content`) shows, under its
// header form, a panel with a search-result preview and a "before you publish"
// checklist. Everything about that panel is authored on the profile:
//
//   "content": {
//     …,
//     "editor_panel": {
//       "enabled": true,                 // false hides the panel (publishing is unaffected)
//       "title": "On the website",
//       "icon": "globe",                 // any PageIcon name (see iconMap)
//       "show_preview": true,            // the search-result preview column
//       "checks": [ ContentCheck, … ]    // ABSENT ⇒ the defaults of the profile's `kind`
//     }
//   }
//
// The renderer knows only the check TYPES below (how to evaluate one); which
// checks a panel runs, on which field, with which limits and wording, is data —
// the per-kind defaults here or the designer's own list. Adding a content kind
// means adding an entry to CONTENT_PANEL_DEFAULTS, never a code branch.

import type { ColumnDef, SqlModel } from './types'
import { richPlainText } from '../richContent'
import { maxTextLength, minTextLength } from './textLength'
import { isMandatory } from './utils'

export type ContentCheckType = 'filled' | 'length' | 'words' | 'headings' | 'images_alt'

export interface ContentCheck {
  /** Stable id — unique within the panel. */
  id: string
  type: ContentCheckType
  /** A content ROLE ('title', 'body', 'price', …), 'seo.<key>', or one of the
   *  search-result fields 'search.title' / 'search.description'. */
  field: string
  /** Text shown in the list. May use {min} {max} {count}. Absent ⇒ generated. */
  label?: string
  /** Lower bound (length: characters, words: words). A `length` check on a
   *  column with its own `min_length` uses the COLUMN's value instead. */
  min?: number
  /** Upper bound in characters (length only). Falls back to the column size. */
  max?: number
  /** false ⇒ kept in the list but not run. */
  enabled?: boolean
  /** Run only when this other field has a value (e.g. alt text only once there is an image). */
  only_if?: string
}

export interface ContentEditorPanel {
  enabled?: boolean
  title?: string
  icon?: string
  show_preview?: boolean
  checks?: ContentCheck[]
}

export interface ContentProfileLite {
  enabled?: boolean
  kind?: string
  label?: { singular?: string; plural?: string }
  roles?: Record<string, string>
  visibility?: { status_column?: string; live_values?: string[]; schedule_column?: string }
  seo?: Record<string, string>
  routes?: { index?: string; item?: string }
  editor_panel?: ContentEditorPanel
}

// ── catalogue (what the designer offers) ─────────────────────────────────

export interface ContentCheckTypeInfo {
  label: string
  description: string
  /** Which bounds the type takes. */
  bounds: Array<'min' | 'max'>
  /** Only meaningful on rich text (the body). */
  richOnly?: boolean
}

export const CONTENT_CHECK_TYPES: Record<ContentCheckType, ContentCheckTypeInfo> = {
  filled: {
    label: 'Is filled in', bounds: [],
    description: 'The field has a value. Skipped on a mandatory column — the form already requires it.',
  },
  length: {
    label: 'Character length', bounds: ['min', 'max'],
    description: 'Number of characters (visible text for rich text). A column with its own Min Length uses that; the column size is the fallback maximum.',
  },
  words: {
    label: 'Word count', bounds: ['min'],
    description: 'At least this many words of visible text.',
  },
  headings: {
    label: 'Has headings', bounds: [], richOnly: true,
    description: 'The rich text is broken up with sub-headings (H2/H3).',
  },
  images_alt: {
    label: 'Images have alt text', bounds: [], richOnly: true,
    description: 'Every image inside the rich text carries a description.',
  },
}

/** The search-result fields a check may target besides the profile's roles. */
export const SEARCH_FIELDS: Record<string, string> = {
  'search.title': 'Search result title',
  'search.description': 'Search result description',
}

// ── defaults per content kind ────────────────────────────────────────────

export const CONTENT_PANEL_BASE: Required<Omit<ContentEditorPanel, 'checks'>> = {
  enabled: true,
  title: 'On the website',
  icon: 'globe',
  show_preview: true,
}

const SEARCH_CHECKS: ContentCheck[] = [
  { id: 'title_filled', type: 'filled', field: 'title', label: 'Has a title' },
  { id: 'title_length', type: 'length', field: 'search.title', min: 20, max: 65, label: 'Title is {min}–{max} characters' },
  { id: 'description_length', type: 'length', field: 'search.description', min: 70, max: 170, label: 'Summary / description is {min}–{max} characters' },
]
const IMAGE_CHECKS: ContentCheck[] = [
  { id: 'image_filled', type: 'filled', field: 'image', label: 'Has an image (used for social sharing)' },
  { id: 'image_alt_filled', type: 'filled', field: 'image_alt', only_if: 'image', label: 'Image has a description' },
]
const words = (min: number): ContentCheck =>
  ({ id: 'body_words', type: 'words', field: 'body', min, label: 'Content has at least {min} words ({count})' })
const LONG_FORM: ContentCheck[] = [
  { id: 'body_headings', type: 'headings', field: 'body', label: 'Content is broken up with headings' },
  { id: 'body_images_alt', type: 'images_alt', field: 'body', label: 'Every image in the content has alt text' },
]
const filled = (field: string, label: string): ContentCheck =>
  ({ id: `${field}_filled`, type: 'filled', field, label })

/** Default checks of each content kind (`transaction.content.kind`). */
export const CONTENT_PANEL_DEFAULTS: Record<string, ContentCheck[]> = {
  article: [...SEARCH_CHECKS, ...IMAGE_CHECKS, words(300), ...LONG_FORM],
  case_study: [...SEARCH_CHECKS, ...IMAGE_CHECKS, words(300), ...LONG_FORM],
  product: [...SEARCH_CHECKS, ...IMAGE_CHECKS,
    filled('price', 'Has a price'), filled('availability', 'Availability is set'), words(80)],
  job: [...SEARCH_CHECKS,
    filled('location', 'Has a location'), filled('employment_type', 'Employment type is set'),
    filled('valid_through', 'Has a closing date'), words(80)],
  event: [...SEARCH_CHECKS, ...IMAGE_CHECKS,
    filled('start_date', 'Has a start date'), filled('location', 'Has a venue'), words(80)],
  person: [...SEARCH_CHECKS, ...IMAGE_CHECKS, filled('job_title', 'Has a job title')],
  department: [...SEARCH_CHECKS, ...IMAGE_CHECKS, words(80)],
  location: [...SEARCH_CHECKS, ...IMAGE_CHECKS, filled('location', 'Has an address'), words(80)],
  custom: [...SEARCH_CHECKS, ...IMAGE_CHECKS, words(80)],
}

/** Defaults for a kind (a kind with no entry gets the generic `custom` set). */
export function defaultContentChecks(kind: string | undefined): ContentCheck[] {
  const list = CONTENT_PANEL_DEFAULTS[kind || ''] || CONTENT_PANEL_DEFAULTS.custom
  return list.map(c => ({ ...c }))
}

export interface ResolvedContentPanel {
  enabled: boolean
  title: string
  icon: string
  show_preview: boolean
  checks: ContentCheck[]
  /** true when the profile carries its own check list (not the kind defaults). */
  customised: boolean
}

/** The panel a profile asks for: its own settings over the base + kind defaults. */
export function resolveContentPanel(profile: ContentProfileLite | undefined | null): ResolvedContentPanel {
  const p = profile?.editor_panel || {}
  const own = Array.isArray(p.checks)
  return {
    enabled: p.enabled !== false,
    title: (p.title || '').trim() || CONTENT_PANEL_BASE.title,
    icon: (p.icon || '').trim() || CONTENT_PANEL_BASE.icon,
    show_preview: p.show_preview !== false,
    checks: own ? (p.checks as ContentCheck[]) : defaultContentChecks(profile?.kind),
    customised: own,
  }
}

/** Whether a transaction model shows the panel at all. */
export function contentPanelVisible(profile: ContentProfileLite | undefined | null): boolean {
  return !!profile && profile.enabled !== false && resolveContentPanel(profile).enabled
}

// ── evaluation ───────────────────────────────────────────────────────────

/** Header-form column definition by designer name / db name (case-insensitive). */
export function contentColumn(sm: SqlModel | undefined, name?: string): ColumnDef | undefined {
  if (!name || !sm?.columns) return undefined
  const low = name.toLowerCase()
  for (const entry of sm.columns) {
    const body = ((entry as { column?: ColumnDef }).column ?? entry) as ColumnDef
    if (String(body?.name || '').toLowerCase() === low || String(body?.db_name || '').toLowerCase() === low) {
      return body
    }
  }
  return undefined
}

/** Form value of a column (form keys are lower-cased names). */
function columnValue(values: Record<string, unknown>, sm: SqlModel | undefined, col?: string): unknown {
  if (!col) return undefined
  const low = col.toLowerCase()
  if (low in values) return values[low]
  if (col in values) return values[col]
  const key = String(contentColumn(sm, col)?.name || '').toLowerCase()
  return key ? values[key] : undefined
}

const text = (v: unknown): string => (v == null ? '' : String(v))

/** Reads the fields a check can name, for one form state. */
export class ContentFields {
  constructor(
    private profile: ContentProfileLite,
    private sm: SqlModel | undefined,
    private values: Record<string, unknown>,
  ) {}

  /** The column NAME a field is stored in (search fields → the column that feeds them). */
  columnName(field: string): string | undefined {
    const roles = this.profile.roles || {}
    const seo = this.profile.seo || {}
    if (field === 'search.title') return roles.title
    if (field === 'search.description') return seo.description || roles.summary
    if (field.startsWith('seo.')) return seo[field.slice(4)]
    return roles[field]
  }

  /** False when the field is not mapped to a column, so a check on it cannot apply. */
  mapped(field: string): boolean {
    if (field === 'search.title') return !!(this.profile.roles || {}).title
    if (field === 'search.description') {
      const r = this.profile.roles || {}
      return !!((this.profile.seo || {}).description || r.summary || r.body)
    }
    return !!this.columnName(field)
  }

  column(field: string): ColumnDef | undefined {
    return contentColumn(this.sm, this.columnName(field))
  }

  /** Raw value (rich text stays HTML). */
  raw(field: string): unknown {
    const roles = this.profile.roles || {}
    const seo = this.profile.seo || {}
    const v = (col?: string) => columnValue(this.values, this.sm, col)
    if (field === 'search.title') return text(v(seo.title)).trim() || text(v(roles.title)).trim()
    if (field === 'search.description') {
      const own = text(v(seo.description)).trim() || richPlainText(v(roles.summary))
      if (own) return own
      const body = richPlainText(v(roles.body))
      return body.length > 160 ? `${body.slice(0, 157).replace(/\s+\S*$/, '')}…` : body
    }
    return v(this.columnName(field))
  }

  /** Visible text of the field (tags stripped). */
  plain(field: string): string {
    return richPlainText(this.raw(field))
  }
}

export interface ContentCheckResult {
  check: ContentCheck
  ok: boolean
  text: string
}

function fill(template: string, vars: Record<string, string | number | undefined>): string {
  return template.replace(/\{(min|max|count)\}/g, (_m, k: string) => {
    const v = vars[k]
    return v == null ? '' : String(v)
  })
}

/** Effective bounds of a length check: the column's own min/max win over the check's fallback. */
export function lengthBounds(check: ContentCheck, column: ColumnDef | undefined): { min?: number; max?: number } {
  const colMin = minTextLength(column) ?? undefined
  const colMax = maxTextLength(column) ?? undefined
  return {
    min: colMin ?? check.min,
    max: check.max != null && colMax != null ? Math.min(check.max, colMax) : (check.max ?? colMax),
  }
}

function fieldLabel(field: string): string {
  if (SEARCH_FIELDS[field]) return SEARCH_FIELDS[field]
  const f = field.replace(/^seo\./, 'SEO ').replace(/_/g, ' ')
  return f.charAt(0).toUpperCase() + f.slice(1)
}

function generatedLabel(check: ContentCheck, b: { min?: number; max?: number }): string {
  const f = fieldLabel(check.field)
  switch (check.type) {
    case 'filled': return `${f} is filled in`
    case 'length':
      return b.min != null && b.max != null ? `${f} is {min}–{max} characters`
        : b.min != null ? `${f} has at least {min} characters` : `${f} is at most {max} characters`
    case 'words': return `${f} has at least {min} words ({count})`
    case 'headings': return `${f} is broken up with headings`
    case 'images_alt': return `Every image in ${f.toLowerCase()} has alt text`
    default: return f
  }
}

/** Run a panel's checks against the current form values. Checks that cannot
 *  apply (field not mapped, switched off, `only_if` unmet, a `filled` check on
 *  a mandatory column, a length check with no bound at all) are left out. */
export function evaluateContentChecks(
  checks: ContentCheck[], fields: ContentFields,
): ContentCheckResult[] {
  const out: ContentCheckResult[] = []
  for (const check of checks) {
    try {
      if (!check || check.enabled === false || !CONTENT_CHECK_TYPES[check.type]) continue
      if (!fields.mapped(check.field)) continue
      if (check.only_if && !fields.plain(check.only_if) && !text(fields.raw(check.only_if)).trim()) continue
      const column = fields.column(check.field)
      let ok = false
      let count: number | undefined
      let b: { min?: number; max?: number } = { min: check.min, max: check.max }
      switch (check.type) {
        case 'filled': {
          // The form already refuses a blank mandatory field — don't repeat it.
          if (column && isMandatory(column)) continue
          ok = !!fields.plain(check.field) || !!text(fields.raw(check.field)).trim()
          break
        }
        case 'length': {
          b = lengthBounds(check, column)
          if (b.min == null && b.max == null) continue
          count = fields.plain(check.field).length
          ok = count > 0 && (b.min == null || count >= b.min) && (b.max == null || count <= b.max)
          break
        }
        case 'words': {
          const t = fields.plain(check.field)
          count = t ? t.split(/\s+/).filter(Boolean).length : 0
          ok = count >= (check.min ?? 1)
          break
        }
        case 'headings':
          ok = /<h[23][\s>]/i.test(text(fields.raw(check.field)))
          break
        case 'images_alt':
          ok = !/<img(?![^>]*\balt="[^"]+")[^>]*>/i.test(text(fields.raw(check.field)))
          break
      }
      const template = (check.label || '').trim() || generatedLabel(check, b)
      out.push({ check, ok, text: fill(template, { min: b.min, max: b.max, count }).replace(/\s+/g, ' ').trim() })
    } catch (err) {
      // A malformed authored check must not take the editor down with it.
      console.warn('[content-panel] check skipped', check, err)
    }
  }
  return out
}
